import { createContext, useContext, useEffect, useMemo } from 'react';
import type { EventIn, Me } from '@nelnela/contracts';
import { createDefaultEventQueue, type EventQueue } from '../lib/events';
import {
  createEmitter,
  endStudySession,
  startStudySession,
  type EmitInput,
  type StudySessionHandle,
} from '../lib/session';

/**
 * The per-launch study session (04-flows "Session & activity lifecycle"): one StudySession per
 * app open, one event queue gated on it having landed, one `emit` for behavior events. Screens
 * get it from `useStudy()`; the Watch screen starts/ends its `activity` inside it.
 */
export interface StudyCtx {
  me: Me;
  session: StudySessionHandle;
  queue: EventQueue;
  emit: (input: EmitInput) => EventIn;
  /** M2: gloss is `entitled` — the sheet shows the calm unentitled line instead of calling */
  entitled: boolean;
}

const StudyContext = createContext<StudyCtx | null>(null);

export function StudyProvider({ me, children }: { me: Me; children: React.ReactNode }) {
  const profileId = me.profiles[0]?.id ?? null;
  const entitled = me.entitlement.active && me.entitlement.plan !== 'none';
  // Keyed on the two facts that matter, not on `me`'s identity: a background /api/me refetch
  // (streak, plan dates) must not rebuild the queue mid-flush.
  const value = useMemo<StudyCtx | null>(() => {
    if (!profileId) return null; // /api/me guarantees a profile; defensive only
    const session = startStudySession(profileId);
    const queue = createDefaultEventQueue(() => session.ready);
    return { me, session, queue, emit: createEmitter(session, queue), entitled };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId, entitled]);
  useEffect(() => {
    if (!value) return;
    value.queue.start();
    // pagehide (not visibilitychange: a backgrounded tab may come back) → best-effort sessionEnd
    const onHide = () => {
      void endStudySession(value.session, { keepalive: true });
    };
    window.addEventListener('pagehide', onHide);
    return () => {
      window.removeEventListener('pagehide', onHide);
      value.queue.stop();
    };
  }, [value]);
  return <StudyContext.Provider value={value}>{children}</StudyContext.Provider>;
}

export function useStudy(): StudyCtx {
  const v = useContext(StudyContext);
  if (!v) throw new Error('useStudy outside StudyProvider (or no profile yet)');
  return v;
}
